import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Eye, Check, AlertTriangle, Edit, Printer } from 'lucide-react';
import { toast } from 'sonner';

export default function StockOpname() {
  const [opnames, setOpnames] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedOpname, setSelectedOpname] = useState(null);
  const [editOpname, setEditOpname] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [opnamesRes, wareRes] = await Promise.all([
        api.get('/stock-opname'),
        api.get('/warehouses')
      ]);
      setOpnames(opnamesRes.data);
      setWarehouses(wareRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (opnameNumber) => {
    if (!window.confirm('Approve stock opname? Stock akan disesuaikan dengan physical count.')) return;
    
    try {
      await api.patch(`/stock-opname/${opnameNumber}/approve`);
      toast.success('Stock opname approved and stock adjusted!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handlePrintOpname = (opname) => {
    if (!opname) return;
    const totalSystem = opname.items.reduce((s, i) => s + (i.system_qty || 0), 0);
    const totalPhysical = opname.items.reduce((s, i) => s + (i.physical_qty || 0), 0);
    const totalVar = opname.total_variance ?? (totalPhysical - totalSystem);
    const rowsHtml = opname.items.map((it, idx) => {
      const v = (it.physical_qty || 0) - (it.system_qty || 0);
      const vColor = v < 0 ? '#b91c1c' : v > 0 ? '#15803d' : '#111';
      return `<tr>
        <td style="text-align:center">${idx + 1}</td>
        <td>${it.item_name || ''}<br/><small style="color:#666">${it.unit || ''}</small></td>
        <td style="text-align:center">${it.system_qty || 0}</td>
        <td style="text-align:center;font-weight:600">${it.physical_qty || 0}</td>
        <td style="text-align:center;font-weight:600;color:${vColor}">${v > 0 ? '+' : ''}${v}</td>
        <td>${it.remark || '-'}</td>
      </tr>`;
    }).join('');
    const totalVarColor = totalVar < 0 ? '#b91c1c' : totalVar > 0 ? '#15803d' : '#111';
    const html = `<!doctype html><html><head><title>Stock Opname ${opname.opname_number}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        h1{font-size:18px;margin:0 0 4px}
        .meta{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:12px 0 16px;font-size:12px}
        .meta div b{display:block;color:#555;font-weight:500;font-size:11px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;vertical-align:top}
        thead{background:#f3f4f6}
        tfoot td{font-weight:700;background:#f9fafb}
        .signatures{margin-top:48px;display:grid;grid-template-columns:repeat(3,1fr);gap:24px;text-align:center;font-size:12px}
        .signatures .sig{border-top:1px solid #333;padding-top:6px;margin-top:64px}
        .notes{margin-top:12px;font-size:12px}
        @media print{button{display:none}}
      </style></head><body>
      <h1>STOCK OPNAME REPORT</h1>
      <div style="font-size:12px;color:#555">Royal Tulip Batu - RTS Inventory</div>
      <div class="meta">
        <div><b>Opname Number</b>${opname.opname_number}</div>
        <div><b>Date</b>${new Date(opname.date).toLocaleDateString('id-ID')}</div>
        <div><b>Warehouse</b>${opname.warehouse || '-'}</div>
        <div><b>Status</b>${opname.status}</div>
        <div><b>Made By</b>${opname.made_by || '-'}</div>
        <div><b>Approved By</b>${opname.approved_by || '-'}</div>
        <div><b>Approved At</b>${opname.approved_at ? new Date(opname.approved_at).toLocaleString('id-ID') : '-'}</div>
        <div><b>Printed</b>${new Date().toLocaleString('id-ID')}</div>
      </div>
      ${opname.notes ? `<div class="notes"><b>Notes:</b> ${opname.notes}</div>` : ''}
      <table>
        <thead><tr><th style="width:32px">#</th><th>Item</th><th style="text-align:center">On Hand Qty</th><th style="text-align:center">Physical Qty</th><th style="text-align:center">Variance</th><th>Remark</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr>
          <td colspan="2" style="text-align:right">TOTAL</td>
          <td style="text-align:center">${totalSystem}</td>
          <td style="text-align:center">${totalPhysical}</td>
          <td style="text-align:center;color:${totalVarColor}">${totalVar > 0 ? '+' : ''}${totalVar}</td>
          <td></td>
        </tr></tfoot>
      </table>
      <div class="signatures">
        <div><div class="sig">Made By<br/><b>${opname.made_by || ''}</b></div></div>
        <div><div class="sig">Approved By<br/><b>${opname.approved_by || ''}</b></div></div>
        <div><div class="sig">Acknowledged By Owner</div></div>
      </div>
      <script>window.onload=()=>{window.print();}</script>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="stock-opname-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">Stock Opname</h1>
          <p className="text-muted-foreground">Physical stock counting and adjustment</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-opname-button">
              <Plus className="h-4 w-4 mr-2" />
              New Stock Opname
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Stock Opname - Physical Count</DialogTitle>
            </DialogHeader>
            <OpnameForm 
              warehouses={warehouses}
              onSuccess={() => {
                setDialogOpen(false);
                fetchData();
              }} 
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-6 bg-blue-50 border-l-4 border-blue-500">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-blue-600 mt-1" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Apa itu Stock Opname?</h3>
            <p className="text-sm text-blue-800">
              Stock opname adalah proses penghitungan fisik barang di gudang untuk memastikan kesesuaian dengan data sistem.
              Proses ini penting untuk mencegah selisih stock dan fraud.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="opname-table">
            <thead>
              <tr className="border-b bg-secondary/50">
                <th className="text-left p-3 font-semibold">Opname Number</th>
                <th className="text-left p-3 font-semibold">Date</th>
                <th className="text-left p-3 font-semibold">Warehouse</th>
                <th className="text-center p-3 font-semibold">Items Count</th>
                <th className="text-right p-3 font-semibold">Total Variance</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-left p-3 font-semibold">Made By</th>
                <th className="text-center p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {opnames.map((opname) => (
                <tr key={opname.opname_number} className="border-b hover:bg-accent/20 transition-colors">
                  <td className="p-3 font-medium">{opname.opname_number}</td>
                  <td className="p-3">{new Date(opname.date).toLocaleDateString('id-ID')}</td>
                  <td className="p-3">{opname.warehouse}</td>
                  <td className="p-3 text-center">{opname.items.length}</td>
                  <td className="p-3 text-right">
                    <span className={`font-semibold ${opname.total_variance === 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {opname.total_variance > 0 ? '+' : ''}{opname.total_variance}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`status-badge ${opname.status === 'Approved' ? 'status-release' : 'status-open'}`}>
                      {opname.status}
                    </span>
                  </td>
                  <td className="p-3">{opname.made_by}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-center space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedOpname(opname);
                          setViewDialogOpen(true);
                          // Refresh data from server for Draft opnames
                          if (opname.status === 'Draft') {
                            api.get(`/stock-opname/${opname.opname_number}`).then(res => {
                              setSelectedOpname(res.data);
                            }).catch(() => {});
                          }
                        }}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {opname.status === 'Draft' && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditOpname(opname);
                              setEditDialogOpen(true);
                            }}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleApprove(opname.opname_number)}
                            data-testid={`approve-opname-${opname.opname_number}`}
                            title="Approve & Adjust Stock"
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Stock Opname Details</span>
              {selectedOpname && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePrintOpname(selectedOpname)}
                  data-testid="print-opname-btn"
                >
                  <Printer className="h-4 w-4 mr-1" /> Print
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedOpname && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Opname Number</Label>
                  <p className="font-medium">{selectedOpname.opname_number}</p>
                </div>
                <div>
                  <Label>Date</Label>
                  <p className="font-medium">{new Date(selectedOpname.date).toLocaleDateString('id-ID')}</p>
                </div>
                <div>
                  <Label>Warehouse</Label>
                  <p className="font-medium">{selectedOpname.warehouse}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <span className={`status-badge ${selectedOpname.status === 'Approved' ? 'status-release' : 'status-open'}`}>
                    {selectedOpname.status}
                  </span>
                </div>
              </div>
              
              {selectedOpname.notes && (
                <div>
                  <Label>Notes</Label>
                  <p className="text-sm">{selectedOpname.notes}</p>
                </div>
              )}

              <div>
                <Label className="mb-2 block">Items Details</Label>
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-secondary/50">
                      <th className="text-left p-2">Item</th>
                      <th className="text-center p-2">On Hand Qty</th>
                      <th className="text-center p-2">Physical Qty</th>
                      <th className="text-center p-2">Variance</th>
                      <th className="text-left p-2">Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOpname.items.map((item, idx) => {
                      const variance = (item.physical_qty || 0) - (item.system_qty || 0);
                      return (
                        <tr key={idx} className="border-b">
                          <td className="p-2">
                            <p className="font-medium">{item.item_name}</p>
                            <p className="text-xs text-muted-foreground">{item.unit}</p>
                          </td>
                          <td className="p-2 text-center tabular-nums">{item.system_qty || 0}</td>
                          <td className="p-2 text-center tabular-nums font-semibold">{item.physical_qty || 0}</td>
                          <td className={`p-2 text-center tabular-nums font-semibold ${variance < 0 ? 'text-red-600' : variance > 0 ? 'text-green-600' : ''}`}>
                            {variance > 0 ? '+' : ''}{variance}
                          </td>
                          <td className="p-2 text-sm text-muted-foreground">{item.remark || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-secondary/30 font-bold">
                      <td className="p-2">Total</td>
                      <td className="p-2 text-center tabular-nums">{selectedOpname.items.reduce((s, i) => s + (i.system_qty || 0), 0)}</td>
                      <td className="p-2 text-center tabular-nums">{selectedOpname.items.reduce((s, i) => s + (i.physical_qty || 0), 0)}</td>
                      <td className={`p-2 text-center tabular-nums ${selectedOpname.total_variance < 0 ? 'text-red-600' : selectedOpname.total_variance > 0 ? 'text-green-600' : ''}`}>
                        {selectedOpname.total_variance > 0 ? '+' : ''}{selectedOpname.total_variance}
                      </td>
                      <td className="p-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedOpname.approved_by && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800">
                    <strong>Approved by:</strong> {selectedOpname.approved_by} on {new Date(selectedOpname.approved_at).toLocaleString('id-ID')}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Stock Opname - {editOpname?.opname_number}</DialogTitle>
          </DialogHeader>
          {editOpname && (
            <OpnameEditForm
              opname={editOpname}
              warehouses={warehouses}
              onSuccess={() => {
                setEditDialogOpen(false);
                setEditOpname(null);
                fetchData();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OpnameForm({ warehouses, onSuccess }) {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    warehouse: '',
    notes: ''
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadStockData = async (warehouse) => {
    if (!warehouse) return;
    
    setLoading(true);
    try {
      const res = await api.get(`/stock-opname/prepare/${warehouse}`);
      setItems(res.data);
    } catch (error) {
      toast.error('Failed to load stock data');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWarehouseChange = (warehouse) => {
    setFormData({ ...formData, warehouse });
    loadStockData(warehouse);
  };

  const handlePhysicalQtyChange = (index, value) => {
    const newItems = [...items];
    const physicalQty = parseFloat(value) || 0;
    newItems[index].physical_qty = physicalQty;
    newItems[index].variance = physicalQty - newItems[index].system_qty;
    setItems(newItems);
  };

  const handleRemarkChange = (index, value) => {
    const newItems = [...items];
    newItems[index].remark = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      toast.error('No items to count');
      return;
    }

    // Check if all items have been counted
    const uncounted = items.filter(item => item.physical_qty === 0 && item.system_qty !== 0);
    if (uncounted.length > 0) {
      if (!window.confirm(`${uncounted.length} items belum dihitung (physical = 0). Lanjutkan?`)) {
        return;
      }
    }

    try {
      await api.post('/stock-opname', {
        ...formData,
        date: new Date(formData.date).toISOString(),
        items: items
      });
      toast.success('Stock opname created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create stock opname');
    }
  };

  const totalVariance = items.reduce((sum, item) => sum + item.variance, 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Date *</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            className="bg-white border-2 border-primary/30"
          />
        </div>
        <div>
          <Label>Warehouse *</Label>
          <Select value={formData.warehouse} onValueChange={handleWarehouseChange}>
            <SelectTrigger className="bg-white border-2 border-primary/30">
              <SelectValue placeholder="Select warehouse" />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map((wh) => (
                <SelectItem key={wh.id} value={wh.id}>
                  {wh.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Notes</Label>
        <Input
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Optional notes"
          className="bg-white border-2 border-primary/30"
        />
      </div>

      {loading && (
        <div className="text-center py-4">
          <p className="text-muted-foreground">Loading stock data...</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-primary/10 p-3 border-b">
            <h3 className="font-semibold text-primary">Physical Count</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Hitung fisik barang di gudang dan masukkan jumlahnya
            </p>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b bg-secondary/50">
                  <th className="text-left p-3">Item Name</th>
                  <th className="text-center p-3">Physical Qty</th>
                  <th className="text-left p-3">Remark</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b hover:bg-accent/10">
                    <td className="p-3">
                      <p className="font-medium">{item.item_name}</p>
                      <p className="text-xs text-muted-foreground">{item.unit}</p>
                    </td>
                    <td className="p-3">
                      <Input
                        type="number"
                        value={item.physical_qty || ''}
                        onChange={(e) => handlePhysicalQtyChange(idx, e.target.value)}
                        placeholder="0"
                        step="0.01"
                        min="0"
                        className="w-24 text-center bg-white border-2 border-primary/30"
                      />
                    </td>
                    <td className="p-3">
                      <Input
                        value={item.remark || ''}
                        onChange={(e) => handleRemarkChange(idx, e.target.value)}
                        placeholder="Optional remark"
                        className="bg-white border-2 border-primary/30"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Info:</strong> Masukkan jumlah fisik hasil penghitungan. Remark dapat diisi untuk catatan seperti kondisi barang, lokasi khusus, dll.
          </p>
        </div>
      )}

      <Button type="submit" className="w-full btn-primary text-lg py-6" disabled={items.length === 0}>
        Create Stock Opname (Draft)
      </Button>
    </form>
  );
}


function OpnameEditForm({ opname, warehouses, onSuccess }) {
  const [items, setItems] = useState(
    opname.items.map(item => ({
      ...item,
      physical_qty: item.physical_qty || 0,
      remark: item.remark || ''
    }))
  );
  const [notes, setNotes] = useState(opname.notes || '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        date: new Date(opname.date).toISOString(),
        warehouse: opname.warehouse,
        notes: notes,
        items: items.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          unit: item.unit,
          system_qty: item.system_qty || 0,
          physical_qty: parseFloat(item.physical_qty) || 0,
          variance: (parseFloat(item.physical_qty) || 0) - (item.system_qty || 0),
          remark: item.remark || ''
        }))
      };
      await api.put(`/stock-opname/${opname.opname_number}`, payload);
      toast.success('Stock opname berhasil diupdate');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Warehouse</Label>
          <p className="font-medium bg-secondary p-2 rounded">{opname.warehouse}</p>
        </div>
        <div>
          <Label>Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan opname" />
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b bg-secondary/50">
              <th className="text-left p-2">Item</th>
              <th className="text-center p-2">Physical Qty</th>
              <th className="text-left p-2">Remark</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} className="border-b">
                <td className="p-2">
                  <p className="font-medium">{item.item_name}</p>
                  <p className="text-xs text-muted-foreground">{item.unit}</p>
                </td>
                <td className="p-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.physical_qty}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[idx].physical_qty = parseFloat(e.target.value) || 0;
                      newItems[idx].variance = newItems[idx].physical_qty - (newItems[idx].system_qty || 0);
                      setItems(newItems);
                    }}
                    className="w-24 text-center mx-auto"
                  />
                </td>
                <td className="p-2">
                  <Input
                    value={item.remark || ''}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[idx].remark = e.target.value;
                      setItems(newItems);
                    }}
                    placeholder="Remark"
                    className="text-sm"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button type="submit" className="w-full btn-primary">
        Update Stock Opname
      </Button>
    </form>
  );
}
